import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateOTRuleDTO,
  CreateOvertimeRequestDTO,
  ListOTRulesQuery,
  ListOvertimeRequestsQuery,
  UpdateOTRuleDTO,
  UpdateOvertimeRequestDTO,
} from "@/modules/hr/overtime/domain/types";
import { overtimeApi } from "./client";

const keys = {
  rules: (q: ListOTRulesQuery) => ["hr", "overtime", "rules", q] as const,
  ruleDetail: (id: string) => ["hr", "overtime", "rules", "detail", id] as const,
  requests: (q: ListOvertimeRequestsQuery) =>
    ["hr", "overtime", "requests", q] as const,
  requestDetail: (id: string) => ["hr", "overtime", "requests", "detail", id] as const,
  requestsSummary: () => ["hr", "overtime", "requests", "summary"] as const,
  approvals: (q: ListOvertimeRequestsQuery) =>
    ["hr", "overtime", "approvals", q] as const,
  approvalsSummary: (periodYear?: number, periodMonth?: number) =>
    ["hr", "overtime", "approvals", "summary", periodYear, periodMonth] as const,
  summaryByMonth: (year: number, month: number) =>
    ["hr", "overtime", "summary", year, month] as const,
};

export function useOTRulesQuery(query: ListOTRulesQuery) {
  return useQuery({
    queryKey: keys.rules(query),
    queryFn: () => overtimeApi.listRules(query),
  });
}

export function useOTRuleDetailQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.ruleDetail(id ?? ""),
    queryFn: () => overtimeApi.getRuleById(id!),
    enabled: !!id,
  });
}

export function useCreateOTRuleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateOTRuleDTO) => overtimeApi.createRule(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "overtime"] });
    },
  });
}

export function useUpdateOTRuleMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateOTRuleDTO }) =>
      overtimeApi.updateRule(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "overtime"] });
    },
  });
}

export function useOvertimeRequestsQuery(query: ListOvertimeRequestsQuery) {
  return useQuery({
    queryKey: keys.requests(query),
    queryFn: () => overtimeApi.listRequests(query),
  });
}

export function useOvertimeRequestSummaryQuery() {
  return useQuery({
    queryKey: keys.requestsSummary(),
    queryFn: () => overtimeApi.getRequestsSummary(),
  });
}

export function useOvertimeRequestDetailQuery(id: string | undefined) {
  return useQuery({
    queryKey: keys.requestDetail(id ?? ""),
    queryFn: () => overtimeApi.getRequestById(id!),
    enabled: !!id,
  });
}

export function useCreateOvertimeRequestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateOvertimeRequestDTO) => overtimeApi.createRequest(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "overtime"] });
    },
  });
}

export function useUpdateOvertimeRequestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateOvertimeRequestDTO }) =>
      overtimeApi.updateRequest(id, dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "overtime"] });
    },
  });
}

export function useApprovalsQuery(query: ListOvertimeRequestsQuery) {
  return useQuery({
    queryKey: keys.approvals(query),
    queryFn: () => overtimeApi.listApprovals(query),
  });
}

export function useApprovalSummaryQuery(
  periodYear?: number,
  periodMonth?: number,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: keys.approvalsSummary(periodYear, periodMonth),
    queryFn: () =>
      overtimeApi.getApprovalSummary(
        periodYear != null || periodMonth != null
          ? { periodYear, periodMonth }
          : undefined,
      ),
    enabled: options?.enabled ?? true,
  });
}

export function useApproveRequestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => overtimeApi.approveRequest(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "overtime"] });
    },
  });
}

export function useRejectRequestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rejectionReason }: { id: string; rejectionReason?: string }) =>
      overtimeApi.rejectRequest(id, rejectionReason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["hr", "overtime"] });
    },
  });
}

export function useOTSummaryByMonthQuery(year: number, month: number) {
  return useQuery({
    queryKey: keys.summaryByMonth(year, month),
    queryFn: () => overtimeApi.getSummaryByMonth(year, month),
    enabled: Number.isInteger(year) && Number.isInteger(month),
  });
}
